import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const API = window.location.origin + '/api';

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);

  const fetchCart = useCallback(async () => {
    if (!user || user === false) { setCartItems([]); setCartCount(0); return; }
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCartItems(data);
      setCartCount(data.reduce((sum, item) => sum + item.quantity, 0));
    } catch (error) {
      console.error('Failed to fetch cart:', error.message);
      setCartItems([]);
      setCartCount(0);
    }
  }, [user]);

  const addToCart = useCallback(async (productId, quantity = 1) => {
    await axios.post(`${API}/cart`, { product_id: productId, quantity }, { withCredentials: true });
    await fetchCart();
  }, [fetchCart]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    await axios.put(`${API}/cart/${productId}?quantity=${quantity}`, {}, { withCredentials: true });
    await fetchCart();
  }, [fetchCart]);

  const removeFromCart = useCallback(async (productId) => {
    await axios.delete(`${API}/cart/${productId}`, { withCredentials: true });
    await fetchCart();
  }, [fetchCart]);

  const clearCart = useCallback(async () => {
    await axios.delete(`${API}/cart`, { withCredentials: true });
    setCartItems([]);
    setCartCount(0);
  }, []);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((sum, item) => {
      if (item.weight_price != null) {
        return sum + item.weight_price * item.quantity;
      }
      const price = item.product?.price || 0;
      const discount = item.product?.discount_percent || 0;
      const finalPrice = discount > 0 ? price * (1 - discount / 100) : price;
      return sum + finalPrice * item.quantity;
    }, 0);
  }, [cartItems]);

  const value = useMemo(() => ({
    cartItems, cartCount, fetchCart, addToCart, updateQuantity, removeFromCart, clearCart, getCartTotal
  }), [cartItems, cartCount, fetchCart, addToCart, updateQuantity, removeFromCart, clearCart, getCartTotal]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
